import { api } from '../../../servicesGlobais/api'
import { Suporte, SuporteFormData, SuporteFilters } from '../tipos/suporte'
import { cleanFilters, extractErrorMessage, toInputDate } from '../../../utils/formatUtils'
import {
  SuporteGeracaoEnvioBuscaFiltro,
  SuporteGeracaoEnvioGerarPayload,
  SuporteGeracaoEnvioResultado,
  SuporteGeracaoEnvioLoteResumo,
  JobStatus,
} from '../tipos/geracaoEnvio'

function normalizeSuporte(backend: any): Suporte {
  const rawDataAbertura = backend.dataAbertura ?? backend.DataAbertura
  const rawDataRecebimento = backend.dataRecebimento ?? backend.DataRecebimento
  const rawDataConclusao = backend.dataConclusao ?? backend.DataConclusao

  return {
    id: backend.id ?? backend.Id ?? backend.idSuporte ?? backend.IdSuporte ?? undefined,
    numeroTicket: backend.numeroTicket ?? backend.NumeroTicket ?? undefined,
    nome: backend.nome ?? backend.Nome ?? '',
    prefClockify: backend.prefClockify ?? backend.PrefClockify ?? undefined,
    ticketUrl: backend.ticketUrl ?? backend.TicketUrl ?? undefined,
    idProjetoClockify: backend.idProjetoClockify ?? backend.IdProjetoClockify ?? undefined,
    solicitanteId: backend.solicitanteId ?? backend.SolicitanteId ?? undefined,
    solicitante: backend.solicitante ?? backend.Solicitante ?? undefined,
    area: backend.area ?? backend.Area ?? undefined,
    dataAbertura: (toInputDate(rawDataAbertura) ?? '') || '',
    dataRecebimento: (toInputDate(rawDataRecebimento) ?? '') || '',
    dataConclusao: rawDataConclusao != null ? (toInputDate(rawDataConclusao) ?? '') : undefined,
    statusCodigo: String(backend.statusCodigo ?? backend.StatusCodigo ?? backend.Status?.Codigo ?? ''),
    prioridadeCodigo: String(backend.prioridadeCodigo ?? backend.PrioridadeCodigo ?? backend.Prioridade?.Codigo ?? ''),
    analistaSMNId: Number(backend.analistaSMNId ?? backend.AnalistaSMNId ?? 0),
    desenvolvedorId: backend.desenvolvedorId ?? backend.DesenvolvedorId ?? undefined,
    solucao: backend.solucao ?? backend.Solucao ?? undefined,
    statusNome: backend.statusNome ?? backend.StatusNome ?? backend.Status?.Descricao ?? undefined,
    prioridadeNome: backend.prioridadeNome ?? backend.PrioridadeNome ?? backend.Prioridade?.Descricao ?? undefined,
    analistaSMNNome: backend.analistaSMNNome ?? backend.AnalistaSMNNome ?? backend.AnalistaSMN?.Nome ?? undefined,
    desenvolvedorNome: backend.desenvolvedorNome ?? backend.DesenvolvedorNome ?? backend.Desenvolvedor?.Nome ?? undefined,
    dataCadastro: backend.dataCadastro ?? backend.DataCadastro ?? undefined,
    dataUltimaAtualizacao: backend.dataUltimaAtualizacao ?? backend.DataUltimaAtualizacao ?? undefined,
  }
}

function convertToFormData(backend: any): SuporteFormData {
  const suporte = normalizeSuporte(backend)

  return {
    id: suporte.id,
    numeroTicket: suporte.numeroTicket ?? '',
    nome: suporte.nome ?? '',
    prefClockify: suporte.prefClockify ?? '',
    ticketUrl: suporte.ticketUrl ?? '',
    idProjetoClockify: suporte.idProjetoClockify ?? undefined,
    solicitanteId: suporte.solicitanteId ?? undefined,
    solicitante: suporte.solicitante ?? '',
    area: suporte.area ?? '',
    dataAbertura: suporte.dataAbertura ?? '',
    dataRecebimento: suporte.dataRecebimento ?? '',
    dataConclusao: suporte.dataConclusao ?? '',
    statusCodigo: suporte.statusCodigo ?? '',
    prioridadeCodigo: suporte.prioridadeCodigo ?? '',
    analistaSMNId: suporte.analistaSMNId ? String(suporte.analistaSMNId) : '',
    desenvolvedorId: suporte.desenvolvedorId ? String(suporte.desenvolvedorId) : '',
    solucao: suporte.solucao ?? '',
  }
}

function convertToBackend(data: SuporteFormData) {
  const dataAberturaVal = data.dataAbertura?.trim()
  const dataRecebimentoVal = data.dataRecebimento?.trim()
  const dataConclusaoVal = data.dataConclusao?.trim()
  const dataRecebimentoFinal = dataRecebimentoVal && dataRecebimentoVal !== ''
    ? dataRecebimentoVal
    : new Date().toISOString()

  return {
    id: data.id ?? 0,
    numeroTicket: data.numeroTicket,
    nome: data.nome,
    prefClockify: data.prefClockify,
    ticketUrl: data.ticketUrl?.trim() || undefined,
    idProjetoClockify: data.idProjetoClockify ?? undefined,
    solicitanteId: data.solicitanteId ?? undefined,
    solicitante: data.solicitante?.trim() || undefined,
    area: data.area?.trim() || undefined,
    dataAbertura: dataAberturaVal || null,
    dataRecebimento: dataRecebimentoFinal,
    dataConclusao: dataConclusaoVal || null,
    statusCodigo: Number(data.statusCodigo) || 0,
    prioridadeCodigo: Number(data.prioridadeCodigo) || 0,
    analistaSMNId: Number(data.analistaSMNId) || 0,
    desenvolvedorId: data.desenvolvedorId ? Number(data.desenvolvedorId) : undefined,
    solucao: data.solucao?.trim() || undefined,
  }
}

export const suporteService = {
  async listarSuportes(filters?: SuporteFilters): Promise<Suporte[]> {
    try {
      const cleanFiltersObj = cleanFilters(filters || {})
      const query = new URLSearchParams(cleanFiltersObj as any).toString()
      const url = `/Suporte${query ? `?${query}` : ''}`
      const suportes = await api.get<any[]>(url)
      return Array.isArray(suportes) ? suportes.map(normalizeSuporte) : []
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar suportes'))
    }
  },

  async getSuporteById(id: number): Promise<SuporteFormData> {
    try {
      const suporte = await api.get<any>(`/Suporte/${id}`)
      return convertToFormData(suporte)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar suporte'))
    }
  },

  async criarSuporte(data: SuporteFormData): Promise<SuporteFormData> {
    try {
      const payload = convertToBackend(data)
      const suporte = await api.post<any>('/Suporte', payload)
      return convertToFormData(suporte)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao criar suporte'))
    }
  },

  async atualizarSuporte(id: number, data: SuporteFormData): Promise<SuporteFormData> {
    try {
      const payload = convertToBackend({ ...data, id })
      const suporte = await api.put<any>(`/Suporte/${id}`, payload)
      return convertToFormData(suporte)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao atualizar suporte'))
    }
  },

  async excluirSuporte(id: number): Promise<void> {
    try {
      await api.delete(`/Suporte/${id}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao excluir suporte'))
    }
  },

  async buscarGeracaoEnvio(filtro: SuporteGeracaoEnvioBuscaFiltro): Promise<SuporteGeracaoEnvioResultado> {
    try {
      const params = new URLSearchParams()
      params.append('dataInicio', filtro.dataInicio)
      if (filtro.dataFim?.trim()) params.append('dataFim', filtro.dataFim)
      if (filtro.desenvolvedorIds && filtro.desenvolvedorIds.length > 0) {
        filtro.desenvolvedorIds.forEach((id) => params.append('desenvolvedorIds', String(id)))
      }

      return await api.get(`/Suporte/geracao-envio?${params.toString()}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar dados da geração para envio'))
    }
  },

  async listarDesenvolvedoresGeracaoEnvio(): Promise<Array<{ pessoaId: number; nome: string }>> {
    try {
      return await api.get('/Suporte/geracao-envio/desenvolvedores')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao carregar desenvolvedores'))
    }
  },

  async listarLotesGeracaoEnvio(): Promise<SuporteGeracaoEnvioLoteResumo[]> {
    try {
      return await api.get('/Suporte/geracao-envio/lotes-reversao')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao carregar lotes de geração para envio'))
    }
  },

  async reverterLoteGeracaoEnvio(loteId: number): Promise<void> {
    try {
      await api.post(`/Suporte/geracao-envio/reverter/${loteId}`, {})
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao reverter lote de geração para envio'))
    }
  },

  async reverterLoteGeracaoEnvioAsync(loteId: number): Promise<{ id: number }> {
    try {
      const res = await api.post<{ id: number }>(`/Suporte/geracao-envio/reverter-async/${loteId}`, {})
      return res
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao iniciar reversão do lote'))
    }
  },

  async gerarPlanilhaGeracaoEnvio(payload: SuporteGeracaoEnvioGerarPayload): Promise<Blob> {
    try {
      return await api.post('/Suporte/geracao-envio/gerar', payload, { responseType: 'blob' })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao gerar planilha de suporte'))
    }
  },

  async criarJobGeracaoEnvio(payload: SuporteGeracaoEnvioGerarPayload): Promise<{ id: number; status: string }> {
    try {
      return await api.post<{ id: number; status: string }>('/Suporte/geracao-envio/jobs', payload)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao criar job de geração'))
    }
  },

  async obterStatusJob(jobId: number): Promise<JobStatus> {
    try {
      return await api.get<JobStatus>(`/Job/${jobId}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao consultar status do job'))
    }
  },

  async downloadJobGeracaoEnvio(jobId: number): Promise<Blob> {
    try {
      return await api.get<Blob>(`/Suporte/geracao-envio/jobs/${jobId}/download`, { responseType: 'blob' })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao baixar planilha'))
    }
  },

  async downloadPlanilhaLote(loteId: number): Promise<Blob> {
    try {
      return await api.get<Blob>(`/Suporte/geracao-envio/lotes/${loteId}/download`, { responseType: 'blob' })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao baixar planilha do lote'))
    }
  },
}
